import { useLayoutEffect, useState } from 'react';

/**
 * Live height of an element, for laying sticky parts out under each other. Returns a
 * callback ref, so it also works for an element that appears after loading.
 */
export const useElementHeight = (): [(element: HTMLElement | null) => void, number] => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return [setElement, height];
};

/**
 * Height of the app's sticky top bar (0 when it scrolls away), so our sticky parts sit
 * under it. The layout can swap the bar after this page mounts, so it is looked up again
 * on every layout change instead of observed once.
 */
export const useAppHeaderHeight = (): number => {
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const measure = () => {
      const header = document.querySelector<HTMLElement>('header.MuiAppBar-root');
      const position = header ? getComputedStyle(header).position : '';
      setHeight(header && (position === 'sticky' || position === 'fixed') ? header.offsetHeight : 0);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, []);

  return height;
};
